import { useSelector } from "react-redux";
import en_CA from "../localization/en_CA";
import fr_CA from "../localization/fr_CA";

const useLocalization = () => {
  const language = useSelector((state) => state.authentication.language);

  const localeCodeMap = {
    en: "en-ca",
    fr: "fr-ca",
  };

  const localeCode = localeCodeMap[language] || "en-ca";

  const localizations = {
    "en-ca": en_CA,
    "fr-ca": fr_CA,
  };

  const localization = localizations[localeCode] || en_CA;

  const getLabel = (label, useCodeAsDefault = true) => {
    if (label in localization.labels) return localization.labels[label];

    if (useCodeAsDefault) return label;

    return "";
  };

  const getTitle = (title) => {
    if (title in localization.titles) return localization.titles[title];

    return title;
  };

  const getText = (text) => {
    if (text in localization.text) return localization.text[text];

    return text;
  };

  const getMessage = (message) => {
    if (message in localization.messages) return localization.messages[message];

    return message;
  };

  const getList = (listName) => {
    if (!localization.lists[listName]) return [];

    return localization.lists[listName];
  };

  const getListItemLabel = (listName, value, short = false) => {
    let list = getList(listName);

    let listItem = list.find((item) => item.value === value);
    if (listItem) {
      if (short) {
        return listItem.short ? listItem.short : listItem.label;
      } else {
        return listItem.label;
      }
    }

    return value;
  };

  const getHelpText = (name) => {
    if (name in localization.help) return localization.help[name];

    return null;
  };

  const displayLocalCurrency = (number, fractions = 2) => {
    if (isNaN(number)) return "";

    return Intl.NumberFormat(localeCode, {
      style: "currency",
      currency: localization.currency,
      maximumFractionDigits: fractions,
    }).format(number);
  };

  const displayLocalNumber = (number) => {
    if (isNaN(number)) return "";

    return Intl.NumberFormat(localeCode).format(number);
  };

  const getMonthOption = (month) => {
    return localization.calendarLists.months.find(
      (item) => item.value === month,
    );
  };

  const sectionTypeLabel = (sectionType) => {
    const type = localization.types.sectionTypes.find(
      (item) => item.id === sectionType,
    );
    if (type) {
      return type.name;
    }

    return localization.labels.unknown;
  };

  const questionTypeLabel = (questionType) => {
    const type = localization.types.questionTypes.find(
      (item) => item.id === questionType,
    );
    if (type) {
      return type.name;
    }

    return localization.labels.unknown;
  };

  const previousAnswerLabel = (previousAnswer) => {
    const type = localization.types.previousAnswerTypes.find(
      (item) => item.id === previousAnswer,
    );
    if (type) {
      return type.name;
    }

    return localization.labels.unknown;
  };

  const provinceList = () => {
    return localization.provinces;
  };

  const getProvinceName = (code) => {
    const province = localization.provinces.find((prov) => prov.id === code);

    if (province) return province.name;

    return "unknown";
  };

  const visibilityFilterName = (name) => {
    const filter = localization.types.visibilityFilters.find(
      (item) => item.id === name,
    );
    return filter?.name;
  };

  const getMonthFullName = (month) => {
    if (month < 0 || month > 11) return null;

    return localization.calendarLists.months[month].name;
  };

  const getMonthShortName = (month) => {
    if (month < 0 || month > 11) return null;

    return localization.calendarLists.months[month].name;
  };

  const getImageResolutionLabel = (id) => {
    if (!id) return "Medium";

    let imageRes = localization.types.imageResolution.find(
      (item) => item.id === id,
    );

    return imageRes?.name;
  };

  const getReport = (reportName) => {
    return localization.reports[reportName];
  };

  return {
    labels: localization.labels,
    messages: localization.messages,
    lists: localization.lists,
    text: localization.text,
    titles: localization.titles,
    calendarLists: localization.calendarLists,
    sectionTypes: localization.types.sectionTypes,
    questionTypes: localization.types.questionTypes,
    previousAnswerTypes: localization.types.previousAnswerTypes,
    visibilityFilters: localization.types.visibilityFilters,
    imageResolution: localization.types.imageResolution,
    imageNumber: localization.types.imageNumber,
    imageType: localization.types.imageType,
    callStatus: localization.types.callStatus,
    dayOfWeek: localization.calendarLists.dow,
    pricingRetailLevels: localization.types.pricingRetailLevels,
    pricingCountryLevels: localization.types.pricingCountryLevels,
    listingRetailLevels: localization.types.listingRetailLevels,
    listingCountryLevels: localization.types.listingCountryLevels,
    sectionTypeLabel,
    questionTypeLabel,
    previousAnswerLabel,
    getLabel,
    getMessage,
    getList,
    getTitle,
    getText,
    getHelpText,
    displayLocalCurrency,
    displayLocalNumber,
    getListItemLabel,
    getMonthOption,
    provinceList,
    provinces: localization.provinces,
    visibilityFilterName,
    getMonthFullName,
    getMonthShortName,
    getProvinceName,
    getImageResolutionLabel,
    distribution: localization.distribution,
    outofstock: localization.outofstock,
    getReport,
  };
};

export default useLocalization;
